import { grey } from '@mui/material/colors';
import Student from '../../../model/Student';
import { Checkbox, TextField, Typography } from '@mui/material';
import CustomTable from '../CustomTable';
import { useContext, useEffect, useState } from 'react';
import { getSecondAStageStudents, updateSecondAStageResult } from '../../../api/content-api';
import { loginPage } from '../../../store/fixed-routing';
import AuthContext from '../../../store/auth-context';
import SnackbarContext from '../../../store/snackbar-context';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../ui/Loader';
import ActionButtons from './ActionButtons';
import Result from '../../../model/Result';
import OlympicsContext from '../../../store/olympics-context';
import { DEFAULT_API_ERROR, DEFAULT_AUTH_ERROR } from '../../../utils/auth-tools';
import { filter } from '../StagesTable';

const SecondAStageTable = () => {
	const [allStudents, setAllStudents] = useState([]);
	const [displayedStudents, setDisplayedStudents] = useState<Student[]>([]);
	const [editedStudent, setEditedStudent] = useState<Student>();
	const [isLoading, setIsLoading] = useState(true);
	const [isUpdating, setIsUpdating] = useState(false);
	const { logout, token, isAdmin, id } = useContext(AuthContext);
	const { olympics, refresh, districtFilterId } = useContext(OlympicsContext);
	const { setMsg } = useContext(SnackbarContext);
	const navigate = useNavigate();
	const editable = olympics && (isAdmin || !olympics?.secondAStagePublished);

	useEffect(() => {
		setDisplayedStudents(filter(allStudents, districtFilterId, id));
	}, [districtFilterId]);

	const downloadData = () => {
		getSecondAStageStudents(token)
			.then((response) => {
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filter(students, districtFilterId, id));
				} else {
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
				setIsLoading(false);
			});
	};

	const handleSaveFirstStageResult = () => {
		setIsUpdating(true);
		const data = {
			id: editedStudent?.id,
			secondAStageTopic: editedStudent?.result.secondAStageTopic,
			secondAOneStagePoints: editedStudent?.result.secondAOneStagePoints,
			secondATwoStagePoints: editedStudent?.result.secondATwoStagePoints,
		};
		updateSecondAStageResult(token, data)
			.then((response) => {
				setIsUpdating(false);
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filter(students, districtFilterId, id));
					setEditedStudent(undefined);
					setMsg({ msg: 'Dane ucznia zostały zaktualizowane', severity: 'success' });
				} else {
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
			})
			.catch((exception) => {
				setIsUpdating(false);
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
				setIsLoading(false);
			});
	};

	useEffect(() => {
		refresh();
		downloadData();
	}, []);

	const handlePointsChange = (event: any, accessor: keyof Result) => {
		if (editedStudent) {
			const result = { ...editedStudent.result };
			// @ts-ignore
			result[accessor] = Number(event.target.value);
			setEditedStudent({
				...editedStudent,
				result: result,
			});
		}
	};

	const handleTextChange = (event: any, accessor: keyof Result) => {
		if (editedStudent) {
			const result = { ...editedStudent.result };
			// @ts-ignore
			result[accessor] = event?.target?.value ? event.target.value.toUpperCase() : '';
			setEditedStudent({
				...editedStudent,
				result: result,
			});
		}
	};

	const renderPointsField = (i: Student, accessor: keyof Result) => {
		return editedStudent?.id === i.id ? (
			<TextField
				variant={'standard'}
				type={'number'}
				value={editedStudent.result[accessor]}
				sx={{
					width: '100%',
					height: '100%',
					justifyContent: 'end',
					'& input': {
						pl: '7%',
						minHeight: '100%',
						margin: 0,
						fontSize: '16px',
						textAlign: 'center',
					},
				}}
				onChange={(event) => handlePointsChange(event, accessor)}
			/>
		) : (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{i.result[accessor]}
			</Typography>
		);
	};

	const renderSumField = (i: Student) => {
		let value = 0;
		if (editedStudent?.id === i.id) {
			value = editedStudent.result.secondAOneStagePoints + editedStudent.result.secondATwoStagePoints;
		} else {
			value = i.result.secondAOneStagePoints + i.result.secondATwoStagePoints;
		}
		value = isNaN(value) ? 0 : value;
		return (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{value}
			</Typography>
		);
	};

	const renderEditableField = (i: Student, accessor: keyof Result) => {
		return editedStudent?.id === i.id ? (
			<TextField
				variant={'standard'}
				value={editedStudent.result[accessor]}
				sx={{
					width: '100%',
					height: '100%',
					justifyContent: 'end',
					'& input': {
						pl: '7%',
						minHeight: '100%',
						margin: 0,
						fontSize: '16px',
						textAlign: 'center',
					},
				}}
				inputProps={{ maxLength: 2 }}
				onChange={(event) => handleTextChange(event, accessor)}
			/>
		) : (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{i.result[accessor]}
			</Typography>
		);
	};

	const data = (input: any) => {
		let additionalColumns: any[] = [];

		if (isAdmin) {
			additionalColumns = [
				{ header: 'Imię', accessor: 'firstName', align: 'left' },
				{ header: 'Nazwisko', accessor: 'lastName', align: 'left' },
			];
		}

		return {
			columns: [
				{ header: 'Lp.', accessor: 'index', align: 'left', width: '60px' },
				{ header: 'Tytuł', accessor: 'personalTitle', align: 'left', width: '100px' },
				...additionalColumns,
				{ header: 'Szkoła', accessor: 'school', align: 'left' },
				{ header: 'Klasa', accessor: 'schoolClass', width: '100px', align: 'center' },
				{
					header: 'Specjalizacja',
					accessor: 'specialization',
					width: '100px',
					align: 'center',
					bgColor: grey[100],
				},
				{ header: 'Temat', accessor: 'subject', align: 'center', editable: editable },
				{ header: 'Punkty I', accessor: 'pointsA', width: '100px', align: 'center', editable: editable },
				{ header: 'Punkty II', accessor: 'pointsB', width: '100px', align: 'center', editable: editable },
				{ header: 'Suma', accessor: 'sum', width: '100px', align: 'center' },
				{ header: 'Zakwalifikowany', accessor: 'qualified', width: '150px', align: 'center' },
				{ header: '', accessor: 'action', align: 'center', width: '100px', nosort: true, editable: editable },
			],

			rows: input
				? input.map((i: Student, index: number) => {
						return {
							index: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{index + 1}
								</Typography>
							),
							personalTitle: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.personalTitle}
								</Typography>
							),
							firstName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.firstName}
								</Typography>
							),
							lastName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.lastName}
								</Typography>
							),
							school: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.school.name}, {i.school.city}
								</Typography>
							),
							schoolClass: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.schoolClass}
								</Typography>
							),
							specialization: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.specialization.name}
								</Typography>
							),
							subject: renderEditableField(i, 'secondAStageTopic'),
							pointsA: renderPointsField(i, 'secondAOneStagePoints'),
							pointsB: renderPointsField(i, 'secondATwoStagePoints'),
							sum: renderSumField(i),
							qualified: (
								<Checkbox
									checked={i.result.secondAStageQualified}
									disabled={true}
								/>
							),
							action: (
								<ActionButtons
									student={i}
									disabled={!editable}
									editedStudent={editedStudent}
									inProgress={isUpdating}
									handleSave={handleSaveFirstStageResult}
									handleClose={() => setEditedStudent(undefined)}
									handleEdit={() => setEditedStudent(i)}
								/>
							),
						};
					})
				: [],
		};
	};
	return <>{isLoading ? <Loader /> : <CustomTable data={data(displayedStudents)} />}</>;
};

export default SecondAStageTable;
